import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LifeBuoy, Search } from 'lucide-react';
import { Button, Card, Input, cn } from '@mlm/ui';
import { ASSISTANT_NAV_MAP } from '@mlm/shared';
import { useStorefrontContent } from '../../storefront/lib/hooks';

/**
 * Scripted help layer — the assistant's NON-AI degradation path. Shown
 * full-page when the assistant feature is off (deep link) and inline when a
 * send fails with `fallback: true` (budget/cap exhausted, key missing,
 * upstream down). Searchable admin-authored FAQ + nav deep links: never a dead
 * button.
 */

/** Curated, always-safe deep links (subset of the assistant's nav map). */
const QUICK_ROUTES = ['/dashboard', '/orders', '/code-vault', '/cash-out', '/earnings', '/faq'];

export function FallbackHelp({ variant }: { variant: 'full' | 'inline' }) {
  const { faq } = useStorefrontContent().data;
  const [query, setQuery] = useState('');
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const entries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faq.entries;
    return faq.entries.filter(
      (e) =>
        e.question.toLowerCase().includes(q) ||
        e.answer.toLowerCase().includes(q) ||
        (e.category ?? '').toLowerCase().includes(q),
    );
  }, [faq.entries, query]);

  const quickLinks = QUICK_ROUTES.map((route) =>
    ASSISTANT_NAV_MAP.find((t) => t.route === route),
  ).filter((t): t is NonNullable<typeof t> => Boolean(t));

  return (
    <Card className={cn('space-y-4 p-4', variant === 'full' && 'p-6')}>
      <div className="flex items-center gap-2">
        <LifeBuoy className="h-5 w-5 text-o" />
        <div>
          <p className="text-sm font-medium">Help topics</p>
          <p className="text-xs text-muted-foreground">
            {variant === 'full'
              ? 'The AI assistant isn’t available right now — here are quick answers and shortcuts instead.'
              : 'While the assistant is unavailable, these might help.'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {quickLinks.map((t) => (
          <Button key={t.route} asChild size="sm" variant="outline">
            <Link to={t.route}>{t.label}</Link>
          </Button>
        ))}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help…"
          className="pl-8"
        />
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matching topics.</p>
      ) : (
        <div className="divide-y">
          {entries.map((e, i) => (
            <div key={`${e.question}-${i}`} className="py-2">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium"
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
              >
                {e.question}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 flex-none text-muted-foreground transition-transform',
                    openIdx === i && 'rotate-180',
                  )}
                />
              </button>
              {openIdx === i && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{e.answer}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
